import {ChevronRightIcon, MinusIcon, PlusIcon} from '@heroicons/react/solid';
import React, {useState} from 'react';
import {Link} from 'react-router-dom';

import {Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import {useCountryList} from '@/context/CountryListContext';
import type {AdminAfriexPerson} from '@/types';
import {mapCountryCodeToFlags} from '@/utils/countries';
import * as formatDate from '@/utils/date';

import UserSummary from './UserSummary';

type ExtraRowArg = AdminAfriexPerson;

const UserTable = ({
  items,
  extraRow,
}: {
  items: AdminAfriexPerson[];
  isMerchant?: boolean;
  extraRow?: (user?: ExtraRowArg) => React.ReactNode;
}) => {
  return (
    <Table>
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-20">&nbsp;</Th>
          <Th>Name</Th>
          <Th>Email</Th>
          <Th>Country</Th>
          <Th>Date</Th>
          <Th className="w-20">&nbsp;</Th>
        </Tr>
      </Thead>

      <TBody>
        {items.map((item, index) => (
          <UserTableRow
            key={index}
            item={item}
            extraRow={extraRow}
            index={index}
          />
        ))}
      </TBody>
    </Table>
  );
};

const UserTableRow = ({
  item,
  extraRow,
  index,
}: {
  item: AdminAfriexPerson;
  isMerchant?: boolean;
  extraRow?: (user?: AdminAfriexPerson) => React.ReactNode;
  index: number;
}) => {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const redirectLink = `/users/${item.id}`;
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';
  const country = item?.currentCountry?.toUpperCase() || '';
  const email = item?.currentEmail || null;
  const flag = country ? mapCountryCodeToFlags(countryList)[country] : null;

  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric>
          <Button onClick={() => setIsExpanded(!isExpanded)} variant="unStyled">
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded && 'bg-cyan-50'
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>

        <Td className="font-medium dark:text-gray-200">
          {item.name?.fullName}
        </Td>
        <Td>{email || '-'}</Td>
        <Td>
          {country || '-'} {flag || ''}
        </Td>
        <Td>{formatDate.DDMonYYYY(item.createdAt?.toString() || '')}</Td>
        <Td>
          <Link to={redirectLink} target="_blank" rel="noopener noreferrer">
            <div className="w-5 h-5 flex justify-center text-gray-700 dark:text-gray-300 hover:border rounded-lg hover:border-gray-300 hover:bg-cyan-50 hover:text-cyan-500">
              <ChevronRightIcon className="w-5 h-5" />
            </div>
          </Link>
        </Td>
      </Tr>

      {isExpanded && (
        <Tr className="pr-5" enableHover={false}>
          <Td colspan={9} className="p-0 m-0">
            <div className="flex py-5 sm:py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <UserSummary
                user={item}
                extraRow={extraRow ? extraRow(item) : null}
              />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default UserTable;
